import { HubConnection, HubConnectionState } from '@microsoft/signalr';
import { AuthContext } from '@urbanx/agx.auth.client';
import { useContext, useEffect, useState } from 'react';
import { ConnectionState } from 'types/pushNotifications';
import { getNegotiatedConnection } from 'utils/signalrConnector';

interface HubConnectionInfo {
  hubConnection: HubConnection | null;
  connectionState: ConnectionState;
}

const HUB_CONNECTION_RETRY_DELAY_MS = 5_000;
// Startup is retried once; established connections use SignalR's automatic reconnect policy.
const MAX_HUB_CONNECTION_START_ATTEMPTS = 2;

const useHubConnection = (hubName: string): HubConnectionInfo => {
  const { getAuthToken } = useContext(AuthContext);
  const [hubConnection, setHubConnection] = useState<HubConnection | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>(
    ConnectionState.Connecting
  );

  useEffect(() => {
    if (!hubName) return;

    const hubConnection = getNegotiatedConnection(hubName, getAuthToken);
    let disposed = false;
    let startAttempts = 0;
    let retryTimeout: ReturnType<typeof setTimeout> | undefined;

    setHubConnection(hubConnection);

    hubConnection.onclose(() => {
      if (disposed) return;
      console.log('Hub closed:', hubName);
      setConnectionState(ConnectionState.Disconnected);
    });

    hubConnection.onreconnecting(() => {
      if (disposed) return;
      console.log('Hub reconnecting:', hubName);
      setConnectionState(ConnectionState.Reconnecting);
    });

    hubConnection.onreconnected(() => {
      if (disposed) return;
      console.log('Hub reconnected:', hubName);
      setConnectionState(ConnectionState.Connected);
    });

    const startConnection = async () => {
      setConnectionState(ConnectionState.Connecting);
      startAttempts += 1;

      try {
        if (hubConnection.state === HubConnectionState.Disconnected) {
          await hubConnection.start();
          if (disposed) return;
          console.log('Hub started:', hubName);
          setConnectionState(ConnectionState.Connected);
        } else if (hubConnection.state === HubConnectionState.Connected) {
          console.log('Hub already connected:', hubName);
          setConnectionState(ConnectionState.Connected);
        }
      } catch (err) {
        if (disposed) return;
        console.error('Error starting hub connection:', hubName, err);
        setConnectionState(ConnectionState.Disconnected);
        if (startAttempts < MAX_HUB_CONNECTION_START_ATTEMPTS) {
          retryTimeout = setTimeout(startConnection, HUB_CONNECTION_RETRY_DELAY_MS);
        }
      }
    };

    void startConnection();

    return () => {
      disposed = true;
      clearTimeout(retryTimeout);
      console.log('Cleaning up hub connection:', hubName);
      if (hubConnection.state !== HubConnectionState.Disconnected) {
        hubConnection.stop().catch((err) => {
          console.error('Error stopping hub connection:', err);
        });
      }
      setHubConnection(null);
    };
  }, [hubName, getAuthToken]);

  return {
    hubConnection,
    connectionState,
  };
};

export default useHubConnection;
